import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
// utilities
import { gaTag } from "@utils/browser-helpers";
import { setInputValue } from "@utils/input-helpers";
import { normalizingString } from "@utils/string-helpers";

const style = {
  selectElement: "w-full border-none outline-none justify-between m-1",
  internalContainerStyle: "inline-flex",
};

const Input = ({
  dataTestID,
  inputType = "text",
  inputName,
  className,
  customId,
  inputWidth,
  customGATag,
  defaultValue = "",
  onChangeCallback,
  onBlurCallback,
  onFocusCallback,
  onKeyDownCallback,
  inputValue = "",
  placeholderText,
  disabled = false,
  disabledStyle = "",
  inputContainerStyle,
  list,
  required,
  externalContainer = false,
  returnErrorStatus,
  icon,
  customInputStyle = { container: "", inputElement: "" },
  autoComplete,
  fillAvailableInput = false,
  getInputRef,
  customStyle = {},
  inputHeight = "h-full",
}) => {
  const [cursorPosition, setCursorPosition] = useState(0);
  const initRef = useRef();
  const preserveCursor = () =>
    initRef && initRef.current
      ? setCursorPosition(initRef.current.selectionStart)
      : null;

  const [value, setValue] = useState(defaultValue || inputValue);

  const [updatedPlaceholderText, setUpdatedPlaceholderText] =
    useState(placeholderText);
  useEffect(() => {
    setUpdatedPlaceholderText(placeholderText);
  }, [placeholderText]);
  useEffect(() => {
    function updateInputValue() {
      setValue(inputValue);
      if (typeof returnErrorStatus === "function") {
        returnErrorStatus(required ? !inputValue : false);
      }
    }
    if (!onChangeCallback && value && inputValue !== value) {
      updateInputValue();
    }
    if (initRef) {
      initRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [inputValue]);

  useEffect(() => {
    if (typeof getInputRef !== "undefined") {
      getInputRef(initRef);
    }
  }, [initRef]);

  const onChangeHandler = (e) => {
    preserveCursor();
    onChangeCallback ? onChangeCallback(e) : setValue(setInputValue(e));
  };
  const onBlurHandler = (e) =>
    onBlurCallback && e.hasOwnProperty("target")
      ? onBlurCallback({
          target: {
            name: e.target.name,
            value: normalizingString(e.target.value),
          },
        })
      : onBlurCallback
      ? onBlurCallback(e)
      : null;
  const onFocusHandler = (e) => (onFocusCallback ? onFocusCallback(e) : null);
  const inputId = `input__${inputName}`;
  const InputContents = () => (
    <>
      <div
        className={`flex min-w-[86%] ${inputHeight} ${
          disabled
            ? disabledStyle
              ? disabledStyle
              : "overflow-hidden !cursor-no-drop !bg-color-disabled-input"
            : ""
        } ${inputWidth} ${inputContainerStyle} ${
          customInputStyle.container
        }`.trim()}
      >
        {icon}
        <input
          id={inputId}
          ref={(el) => (initRef.current = el)}
          type={inputType}
          data-ga_click_id={customGATag || `input: ${inputName}`}
          data-ga_tag={gaTag()}
          data-testid={dataTestID || inputId}
          className={` ${
            disabledStyle ? "!cursor-no-drop" : ""
          } font-body text-xs-3 tracking-subhead leading-subhead text-opacity-87 font-normal shadow-none ${className} ${
            style.selectElement
          } ${customInputStyle.inputElement} ${
            fillAvailableInput ? "w-full" : ""
          }`.trim()}
          style={customStyle}
          onChange={(e) => onChangeHandler(e)}
          onBlur={(e) => onBlurHandler(e)}
          onFocus={(e) => onFocusHandler(e)}
          onKeyDown={(e) => (onKeyDownCallback ? onKeyDownCallback(e) : {})}
          placeholder={
            disabled && !(inputType === "password")
              ? "Disabled"
              : updatedPlaceholderText
          }
          name={`input-${inputName}`}
          value={
            disabledStyle
              ? updatedPlaceholderText
              : onChangeCallback
              ? inputValue
              : value
          }
          disabled={disabled}
          list={list}
          autoComplete={autoComplete ? "on" : "off"}
          required={required}
          title={updatedPlaceholderText}
        />
      </div>
    </>
  );

  return externalContainer ? (
    InputContents()
  ) : (
    <div className={`${style.internalContainerStyle} ${inputWidth}`}>
      {InputContents()}
    </div>
  );
};

Input.propTypes = {
  /** determines type of input */
  inputType: PropTypes.string,
  /** sets name to input field determines input id `input__${inputName}` and className tag `${inputName}-input-field*/
  inputName: PropTypes.string.isRequired,
  /** overwrites `input: ${customId || inputName} */
  customGATag: PropTypes.string,
  /** sets custom id overrides inputName `input__${customId} */
  customId: PropTypes.string,
  /** bool to determine if parent container acts as wrapper for input items */
  externalContainer: PropTypes.bool,
  /** id target of datalist */
  list: PropTypes.string,
  /** bool to determine if input is required */
  required: PropTypes.bool,
  /** custom input width set as "##rem" or other css size */
  inputWidth: PropTypes.string,
  /** optional function triggered when input changes */
  onChangeCallback: PropTypes.func,
  /** optional function triggered when user tabs out or clicks outside of field */
  onBlurCallback: PropTypes.func,
  /** optional function triggered when input is focused */
  onFocusCallback: PropTypes.func,
  /** default inputValue */
  inputValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** placeholder text, "Disabled" if disabled flag is true and disabledValue is null */
  placeholderText: PropTypes.string,
  /** disables input if true, changes placeholderText to "Disabled" if disabledValue is null */
  disabled: PropTypes.bool,
  /** function that retrieves and handles result of null input if required */
  returnErrorStatus: PropTypes.func,
  /** adds class vendor prefixes for input elem filling entire width */
  fillAvailableInput: PropTypes.bool,
  /** Input DOM ref */
  getInputRef: PropTypes.func,
};

export default Input;
